import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axios';
import logo from '../wysupp-icon-green-transparent.svg';
import AccountInfo from '../AccountInfo';
import { useAuth } from '../AuthContext';
import { FaArrowLeft, FaSearch, FaUndo, FaFileCsv, FaEdit, FaChevronDown } from 'react-icons/fa';

// Custom hook to detect clicks outside a component
const useClickOutside = (ref, handler) => {
    useEffect(() => {
        const listener = (event) => {
            if (!ref.current || ref.current.contains(event.target)) {
                return;
            }
            handler(event);
        };
        document.addEventListener('mousedown', listener);
        document.addEventListener('touchstart', listener);
        return () => {
            document.removeEventListener('mousedown', listener);
            document.removeEventListener('touchstart', listener);
        };
    }, [ref, handler]);
};

const AVAILABLE_ROLES = [
    { key: 'admin', label: 'Admin' },
    { key: 'editor', label: 'Editor' },
    { key: 'viewer', label: 'Viewer' },
];

const SORT_OPTIONS = [
    { key: 'username_asc', label: 'Name (A-Z)', value: { by: 'username', order: 'asc' } },
    { key: 'username_desc', label: 'Name (Z-A)', value: { by: 'username', order: 'desc' } },
    { key: 'role_asc', label: 'Permission (A-Z)', value: { by: 'role', order: 'asc' } },
    { key: 'role_desc', label: 'Permission (Z-A)', value: { by: 'role', order: 'desc' } },
    { key: 'status_desc', label: 'Status (Active > Inactive)', value: { by: 'is_active', order: 'desc' } },
    { key: 'status_asc', label: 'Status (Inactive > Active)', value: { by: 'is_active', order: 'asc' } },
];

export default function EditUserListPage() {
    const [users, setUsers] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isExporting, setIsExporting] = useState(false);

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [selectedProjects, setSelectedProjects] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [sortConfig, setSortConfig] = useState(SORT_OPTIONS[0].key);

    // Autocomplete
    const [autocompleteSuggestions, setAutocompleteSuggestions] = useState([]);
    const autocompleteTimeoutRef = useRef(null);
    const navigate = useNavigate();

    // Custom Dropdown
    const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
    const projectDropdownRef = useRef(null);
    useClickOutside(projectDropdownRef, () => setIsProjectDropdownOpen(false));

    const { user, selectedProjectName } = useAuth();

    const fetchProjects = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/projects/');
            setProjects(response.data || []);
        } catch (err) {
            // --- FIX: Prevent console errors on timeout ---
            if (err.response?.status === 401) return;
            console.error("Error fetching projects:", err);
        }
    }, []);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const queryParams = new URLSearchParams();
            if (debouncedSearchTerm) queryParams.append('search', debouncedSearchTerm);
            if (selectedStatus) queryParams.append('status', selectedStatus);

            selectedRoles.forEach(role => queryParams.append('roles', role));

            if (selectedProjects.length > 0) {
                selectedProjects.forEach(project => queryParams.append('projects', project));
            } else if (user?.role === 'manager') {
                user.projects?.forEach(p => queryParams.append('projects', p.project_name));
            }

            const currentSort = SORT_OPTIONS.find(opt => opt.key === sortConfig)?.value;
            if (currentSort) {
                queryParams.append('sort_by', currentSort.by);
                queryParams.append('order', currentSort.order);
            }

            const response = await axiosInstance.get(`/api/users?${queryParams.toString()}`);
            setUsers(response.data || []);
            setLoading(false); // Stop loading on success
        } catch (err) {
            // --- TIMEOUT FIX: Prevent red error flash on timeout ---
            if (err.response?.status === 401) return;

            setError(err.response?.data?.detail || 'Failed to fetch users.');
            setLoading(false); // Stop loading on actual errors
        }
    }, [debouncedSearchTerm, selectedRoles, selectedProjects, selectedStatus, sortConfig, user]);

    const fetchAutocompleteSuggestions = useCallback(async (query) => {
        if (!query.trim()) {
            setAutocompleteSuggestions([]);
            return;
        }
        try {
            const queryParams = new URLSearchParams();
            queryParams.append('q', query);

            if (selectedProjects.length > 0) {
                selectedProjects.forEach(project => queryParams.append('projects', project));
            }
            else if (user?.role === 'manager') {
                user.projects?.forEach(p => queryParams.append('projects', p.project_name));
            }

            const response = await axiosInstance.get(`/api/v1/users/autocomplete?${queryParams.toString()}`);
            setAutocompleteSuggestions(response.data || []);
        } catch (err) {
            // --- TIMEOUT FIX: Prevent autocomplete collapse/error on timeout ---
            if (err.response?.status === 401) return;

            console.error("Error fetching autocomplete suggestions:", err);
            setAutocompleteSuggestions([]);
        }
    }, [selectedProjects, user]);

    useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => {
            clearTimeout(timer);
        };
    }, [searchTerm]);

    useEffect(() => {
        if (autocompleteTimeoutRef.current) clearTimeout(autocompleteTimeoutRef.current);
        if (searchTerm.length >= 2) {
            autocompleteTimeoutRef.current = setTimeout(() => fetchAutocompleteSuggestions(searchTerm), 300);
        } else {
            setAutocompleteSuggestions([]);
        }
    }, [searchTerm, fetchAutocompleteSuggestions]);

    const handleMultiSelectToggle = (setter, currentValues, value) => {
        const newValues = currentValues.includes(value) ? currentValues.filter(item => item !== value) : [...currentValues, value];
        setter(newValues);
    };

    const handleSelectSuggestion = (suggestion) => {
        setSearchTerm(suggestion.username || suggestion.email);
        setAutocompleteSuggestions([]);
    };

    const handleExportCsv = () => {
        setIsExporting(true);
        const headers = ["Username", "Email", "Permission", "Status", "Projects"];
        const csvRows = users.map(user => {
            const escapeCsv = (val) => `"${String(val ?? '').replace(/"/g, '""')}"`;
            const projectsList = user.projects?.map(p => p.project_name).join('; ') || "No projects";
            return [escapeCsv(user.username), escapeCsv(user.email), escapeCsv(user.role), escapeCsv(user.is_active ? 'Active' : 'Inactive'), escapeCsv(projectsList)].join(',');
        });
        const blob = new Blob([[headers.join(','), ...csvRows].join('\n')], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'users_list.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setIsExporting(false);
    };

    const handleResetFilters = () => {
        setSearchTerm(''); setSelectedRoles([]); setSelectedProjects([]);
        setSelectedStatus(''); setSortConfig(SORT_OPTIONS[0].key);
        setAutocompleteSuggestions([]);
    };

    return (
        <div className="admin-page-container">
            <header className="dashboard-header">
                <Link to="/home" style={{ textDecoration: 'none' }}>
                    <div className="logo-section" style={{ cursor: 'pointer' }}>
                        <img src={logo} className="logo-icon" alt="Wysupp Logo" />
                        <span className="logo-text">Wysupp Validate</span>
                        <span className="logo-subtext">Data Quality Management</span>
                    </div>
                </Link>
                <AccountInfo />
            </header>

            <main className="admin-main-content">
                <div className="page-header">
                    <button onClick={() => navigate('/admin/manage-users')} className="back-button"><FaArrowLeft /> Back</button>
                    <h1 className="page-title">Edit Users</h1>
                </div>

                <div className="filter-card">
                    <h2 className="filter-title">Filters & Sorting</h2>
                    <div className="filter-grid">
                        <div className="filter-item search-item">
                            <label htmlFor="search">Search</label>
                            <div className="input-group">
                                <FaSearch className="input-icon" />
                                <input id="search" type="text" placeholder="Search by name or email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} autoComplete="off" />
                            </div>
                            {autocompleteSuggestions.length > 0 && (
                                <ul className="autocomplete-suggestions">
                                    {autocompleteSuggestions.map((user, index) => (
                                        <li key={index} onClick={() => handleSelectSuggestion(user)}>
                                            <span className="suggestion-username">{user.username}</span>
                                            <span className="suggestion-email">{user.email}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div className="filter-item">
                            <label>Permissions</label>
                            <div className="filter-buttons">
                                {AVAILABLE_ROLES.map(role => (
                                    <button key={role.key} onClick={() => handleMultiSelectToggle(setSelectedRoles, selectedRoles, role.key)} className={`filter-button ${selectedRoles.includes(role.key) ? 'active' : ''}`}>{role.label}</button>
                                ))}
                            </div>
                        </div>

                        <div className="filter-item" ref={projectDropdownRef}>
                            <label>Projects</label>
                            <div className="custom-dropdown">
                                <button type="button" className="dropdown-trigger" onClick={() => setIsProjectDropdownOpen(!isProjectDropdownOpen)}>
                                    <span>{selectedProjects.length > 0 ? `${selectedProjects.length} selected` : 'All Projects'}</span>
                                    <FaChevronDown className={`dropdown-arrow ${isProjectDropdownOpen ? 'open' : ''}`} />
                                </button>
                                {isProjectDropdownOpen && (
                                    <div className="dropdown-panel custom-scrollbar">
                                        <ul>
                                            {projects.map(p => (
                                                <li key={p.id}>
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedProjects.includes(p.project_name)}
                                                            onChange={() => handleMultiSelectToggle(setSelectedProjects, selectedProjects, p.project_name)}
                                                        />
                                                        {p.project_name}
                                                    </label>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="filter-item">
                            <label htmlFor="status">Status</label>
                            <select id="status" value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}>
                                <option value="">All</option>
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </select>
                        </div>

                        <div className="filter-item">
                            <label htmlFor="sort">Sort by</label>
                            <select id="sort" value={sortConfig} onChange={e => setSortConfig(e.target.value)}>
                                {SORT_OPTIONS.map(opt => (<option key={opt.key} value={opt.key}>{opt.label}</option>))}
                            </select>
                        </div>
                    </div>
                    <div className="filter-actions">
                        <button onClick={handleResetFilters} className="action-button reset-button"><FaUndo /> Reset Filters</button>
                        <button onClick={handleExportCsv} disabled={isExporting || users.length === 0} className="action-button export-button"><FaFileCsv />{isExporting ? 'Exporting...' : 'Export CSV'}</button>
                    </div>
                </div>

                <div className="table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Username</th><th>Email</th><th>Permission</th><th>Projects</th><th>Status</th>
                                <th className="actions-column">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan="6" className="table-message">Loading users...</td></tr>}
                            {error && <tr><td colSpan="6" className="table-message error">{error}</td></tr>}
                            {!loading && !error && users.length === 0 && <tr><td colSpan="6" className="table-message">No users found with the current filters.</td></tr>}
                            {!loading && !error && users.map(targetUser => (
                                <tr key={targetUser.id}>
                                    <td data-label="Username">{targetUser.username}</td>
                                    <td data-label="Email">{targetUser.email || 'N/A'}</td>
                                    <td data-label="Permission" className="capitalize">{targetUser.role}</td>
                                    <td data-label="Projects">{targetUser.projects?.map(p => p.project_name).join(', ') || 'None'}</td>
                                    <td data-label="Status"><span className={`status-badge ${targetUser.is_active ? 'active' : 'inactive'}`}>{targetUser.is_active ? 'Active' : 'Inactive'}</span></td>
                                    <td data-label="Actions" className="actions-column">
                                        {/* --- UPDATED: Managers cannot edit Admins --- */}
                                        {/* Only show the button if the current user is NOT a manager OR if the target is NOT an admin */}
                                        {!(user?.role === 'manager' && targetUser.role === 'admin') && (
                                            <button onClick={() => navigate(`/admin/manage-users/edit-user/${targetUser.id}`)} className="action-button edit-button"><FaEdit /> Edit</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </main>
            <style>{`
                :root {
                    --dark-bg: #1a202c; --card-bg: #2d3748; --text-color: #e2e8f0; --light-text: #a0aec0;
                    --green-accent: #48bb78; --border-color: #4a5568; --input-bg: #1a202c;
                }
                .admin-page-container, .admin-page-container * {
                    box-sizing: border-box;
                }

                .admin-page-container {
                    background-color: var(--dark-bg);
                    min-height: 100vh;
                    color: var(--text-color);
                    font-family: 'Inter', sans-serif;
                }
                .dashboard-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem 3rem;
                    background-color: var(--card-bg);
                    border-bottom: 1px solid var(--border-color);
                }
                .logo-section { display: flex; align-items: center; gap: 0.5rem; }
                .logo-icon { width: 30px; height: 30px; }
                .logo-text { font-size: 1.25rem; font-weight: 700; color: var(--green-accent); }
                .admin-main-content {
                    max-width: 1400px;
                    margin: 2rem auto;
                    margin-bottom: 0rem;
                    padding: 2rem;
                }
                .page-header {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    margin-bottom: 1rem;
                }
                .back-button {
                    background: var(--card-bg); color: var(--text-color); border: 1px solid var(--border-color);
                    padding: 0.5rem 1rem; border-radius: 6px; font-weight: 600; cursor: pointer;
                    transition: background-color 0.2s; display: flex; align-items: center; gap: 0.5rem;
                }
                .back-button:hover { background: #4a5568; }
                .page-title {
                    font-size: 2rem;
                    font-weight: 700;
                    color: var(--text-color);
                }

                /* Filter Card */
                .filter-card {
                    background-color: var(--card-bg);
                    border: 1px solid var(--border-color);
                    border-radius: 12px;
                    padding: 1.5rem 2rem;
                    margin-bottom: 2rem;
                }
                .filter-title {
                    font-size: 1.25rem;
                    font-weight: 600;
                    margin-bottom: 1.5rem;
                }
                .filter-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
                    gap: 1.5rem;
                    align-items: end;
                }
                .filter-item {
                    display: flex;
                    flex-direction: column;
                }
                .filter-item label {
                    font-size: 0.875rem;
                    font-weight: 500;
                    color: var(--light-text);
                    margin-bottom: 0.5rem;
                }
                .filter-item input, .filter-item select {
                    width: 100%;
                    height: 42px;
                    background-color: var(--input-bg);
                    border: 1px solid var(--border-color);
                    border-radius: 6px;
                    color: var(--text-color);
                    font-size: 1rem;
                    transition: border-color 0.2s;
                    padding: 0.75rem;
                }
                .filter-item input {
                    padding-left: 2.5rem;
                }
                .filter-item input:focus, .filter-item select:focus {
                    outline: none;
                    border-color: var(--green-accent);
                }
                .input-group { position: relative; }
                .input-icon {
                    position: absolute;
                    top: 50%;
                    left: 0.75rem;
                    transform: translateY(-50%);
                    color: var(--light-text);
                }
                .search-item {
                    position: relative;
                    grid-column: 1 / -1;
                }
                .autocomplete-suggestions {
                    position: absolute; top: 100%; left: 0; right: 0;
                    background: var(--card-bg); border: 1px solid var(--border-color);
                    border-top: none; border-radius: 0 0 6px 6px;
                    list-style: none; padding: 0.5rem; margin: 0;
                    z-index: 10; max-height: 200px; overflow-y: auto;
                }
                .autocomplete-suggestions li {
                    display: flex; align-items: baseline; gap: 0.75rem;
                    padding: 0.75rem; border-radius: 4px; cursor: pointer;
                }
                .autocomplete-suggestions li:hover { background-color: #4a5568; }
                .suggestion-username { font-weight: 500; color: var(--text-color); }
                .suggestion-email { font-size: 0.85rem; color: var(--light-text); }
                
                .filter-buttons { display: flex; flex-wrap: wrap; gap: 0.5rem; }
                .filter-button {
                    background-color: var(--input-bg); border: 1px solid var(--border-color);
                    color: var(--light-text); padding: 0.4rem 0.8rem; border-radius: 6px;
                    cursor: pointer; transition: all 0.2s; font-size: 0.85rem; font-weight: 500;
                }
                .filter-button:hover {
                    border-color: var(--light-text);
                    color: var(--text-color);
                }
                .filter-button.active {
                    background-color: var(--green-accent);
                    border-color: var(--green-accent);
                    color: var(--dark-bg);
                    font-weight: 600;
                }
                
                .custom-dropdown { position: relative; }
                .dropdown-trigger {
                    display: flex; justify-content: space-between; align-items: center;
                    width: 100%; height: 42px; background-color: var(--input-bg);
                    border: 1px solid var(--border-color); border-radius: 6px;
                    color: var(--text-color); font-size: 1rem; padding: 0.75rem;
                    cursor: pointer; text-align: left;
                }
                .dropdown-arrow { transition: transform 0.2s ease; }
                .dropdown-arrow.open { transform: rotate(180deg); }
                .dropdown-panel {
                    position: absolute; top: calc(100% + 5px); left: 0; right: 0;
                    background-color: var(--card-bg); border: 1px solid var(--border-color);
                    border-radius: 6px; z-index: 20; max-height: 200px; overflow-y: auto;
                }
                .dropdown-panel ul { list-style: none; padding: 0.5rem; margin: 0; }
                .dropdown-panel li label {
                    display: flex; align-items: center; padding: 0.5rem;
                    border-radius: 4px; cursor: pointer; width: 100%;
                }
                .dropdown-panel li:hover { background-color: #4a5568; }
                .dropdown-panel input[type="checkbox"] {
                    margin-right: 0.75rem; width: 16px; height: 16px;
                    accent-color: var(--green-accent);
                }

                .filter-actions {
                    display: flex; justify-content: flex-end; gap: 1rem;
                    margin-top: 1.5rem; padding-top: 1.5rem;
                    border-top: 1px solid var(--border-color);
                }
                .action-button {
                    padding: 0.6rem 1.2rem; border: none; border-radius: 6px;
                    font-weight: 600; cursor: pointer; transition: all 0.2s;
                    display: flex; align-items: center; gap: 0.5rem;
                }
                .reset-button { background-color: #ef4444; color: white; }
                .reset-button:hover { background-color: #dc2626; }
                .export-button { background-color: #16a34a; color: white; }
                .export-button:hover { background-color: #15803d; }
                .action-button:disabled { background-color: #4a5568; cursor: not-allowed; }

                .custom-scrollbar::-webkit-scrollbar { width: 8px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: var(--input-bg); }
                .custom-scrollbar::-webkit-scrollbar-thumb { background-color: var(--border-color); border-radius: 8px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: var(--light-text); }

                .table-wrapper { overflow-x: auto; }
                .admin-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
                .admin-table th, .admin-table td {
                    padding: 1rem 1.25rem; text-align: left;
                    white-space: normal; word-break: break-word;
                }
                .admin-table thead { background-color: #1a202c; }
                .admin-table th {
                    font-size: 0.75rem; font-weight: 600; color: var(--light-text);
                    text-transform: uppercase; letter-spacing: 0.05em;
                }
                .admin-table tbody tr { border-top: 1px solid var(--border-color); }
                .admin-table tbody tr:hover { background-color: #374151; }
                .admin-table td.capitalize { text-transform: capitalize; }
                .actions-column { text-align: right; }
                .edit-button { background-color: #3b82f6; color: white; }
                .edit-button:hover { background-color: #2563eb; }
                .status-badge { padding: 0.25rem 0.75rem; border-radius: 99px; font-size: 0.75rem; font-weight: 600; }
                .status-badge.active { background-color: rgba(72, 187, 120, 0.2); color: #6ee7b7; }
                .status-badge.inactive { background-color: rgba(107, 114, 128, 0.2); color: #9ca3af; }
                .table-message { text-align: center; padding: 2rem; color: var(--light-text); font-size: 1.1rem; }
                .table-message.error { color: #fca5a5; font-weight: 600; }
                
                @media (max-width: 900px) {
                    .admin-table thead { display: none; }
                    .admin-table, .admin-table tbody, .admin-table tr, .admin-table td { display: block; width: 100%; }
                    .admin-table tr { margin-bottom: 1rem; border: 1px solid var(--border-color); border-radius: 8px; }
                    .admin-table td { text-align: right; padding-left: 50%; position: relative; border-top: none; }
                    .admin-table td::before {
                        content: attr(data-label);
                        position: absolute; left: 1.25rem; width: calc(50% - 2.5rem);
                        text-align: left; font-weight: 600; color: var(--light-text);
                        text-transform: uppercase; font-size: 0.75rem;
                    }
                    .actions-column { text-align: center; }
                    .actions-column button { width: 100%; justify-content: center; }
                }
                .logo-subtext {
                    font-size: 0.75rem;
                    color: var(--light-text);
                    margin-left: 0.5rem;
                    padding-left: 0.5rem;
                     border-left: 1px solid var(--light-text);
                }
            `}</style>
        </div>
    );
}